// src/pages/ShipmentsList.jsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { db } from "../services/firebase";

const PAGE_SIZE = 20;

function fmtDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString();
}

function computeCounts(lines = []) {
  let shipped = 0;
  let received = 0;
  for (const l of lines) {
    shipped += Number(l?.qty || 0);
    received += Number(l?.rcvdQty || 0);
  }
  return { shipped, received };
}

function StatusBadge({ status }) {
  const map = {
    draft: "secondary",
    shipped: "primary",
    partial: "warning",
    received: "success",
  };
  const color = map[status] || "secondary";
  return <span className={`badge bg-${color}`}>{status || "draft"}</span>;
}

export default function ShipmentsList() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("all"); // all|draft|shipped|partial|received
  const [needle, setNeedle] = useState("");
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [msg, setMsg] = useState("");

  // ref stable vers "shipments"
  const baseRef = React.useMemo(() => collection(db, "shipments"), []);

  // buildQuery ne dépend que de status (et de baseRef mémoïsé)
  const buildQuery = React.useCallback(() => {
    if (status !== "all") {
      return query(
        baseRef,
        where("status", "==", status),
        orderBy("createdAt", "desc"),
        limit(PAGE_SIZE)
      );
    }
    return query(baseRef, orderBy("createdAt", "desc"), limit(PAGE_SIZE));
  }, [baseRef, status]);

  // Chargement initial + recherche
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setItems([]);
      setCursor(null);
      setDone(false);
      setLoading(true);
      setMsg("");

      try {
        const q = buildQuery();
        const snap = await getDocs(q);
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data(), _doc: d }));

        const n = needle.trim().toLowerCase();
        const filtered = n
          ? rows.filter((r) =>
              (
                `${r.id} ${r.code || ""} ${r.origin || ""} ${r.destination || ""}`
              )
                .toLowerCase()
                .includes(n)
            )
          : rows;

        if (cancelled) return;

        setItems(filtered);
        if (snap.docs.length < PAGE_SIZE) {
          setDone(true);
          setCursor(null);
        } else {
          setDone(false);
          setCursor(snap.docs[snap.docs.length - 1]);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setMsg("Erreur de chargement des shippings.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    // petit debounce sur la recherche
    const t = setTimeout(run, 250);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [status, needle, buildQuery]);

  // Pagination ("Charger plus")
  async function fetchMore() {
    if (!cursor || loading) return;
    setLoading(true);
    try {
      const q = query(buildQuery(), startAfter(cursor));
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data(), _doc: d }));

      const n = needle.trim().toLowerCase();
      const filtered = n
        ? rows.filter((r) =>
            (
              `${r.id} ${r.code || ""} ${r.origin || ""} ${r.destination || ""}`
            )
              .toLowerCase()
              .includes(n)
          )
        : rows;

      setItems((prev) => [...prev, ...filtered]);
      if (snap.docs.length < PAGE_SIZE) {
        setDone(true);
        setCursor(null);
      } else {
        setDone(false);
        setCursor(snap.docs[snap.docs.length - 1]);
      }
    } catch (e) {
      console.error(e);
      setMsg("Erreur de chargement des shippings.");
    } finally {
      setLoading(false);
    }
  }

  const empty = !loading && items.length === 0;

  return (
    <div className="container py-3">
      <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
        <h3 className="m-0 me-auto">Shippings</h3>

        <div>
          <label className="form-label small mb-1">Statut</label>
          <select
            className="form-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={{ minWidth: 180 }}
          >
            <option value="all">Tous</option>
            <option value="draft">draft</option>
            <option value="shipped">shipped</option>
            <option value="partial">partial</option>
            <option value="received">received</option>
          </select>
        </div>

        <div>
          <label className="form-label small mb-1">Recherche</label>
          <input
            className="form-control"
            placeholder="ID, code, origine/destination…"
            value={needle}
            onChange={(e) => setNeedle(e.target.value)}
            style={{ minWidth: 260 }}
          />
        </div>
      </div>

      {msg && <div className="alert alert-warning py-2">{msg}</div>}

      {empty ? (
        <div className="text-muted">Aucun shipping.</div>
      ) : (
        <div className="row g-3">
          {items.map((s) => {
            const lines = Array.isArray(s.lines) ? s.lines : [];
            const { shipped, received } = computeCounts(lines);
            const progress =
              shipped > 0 ? Math.min(100, Math.round((received / shipped) * 100)) : 0;

            return (
              <div className="col-12 col-md-6 col-lg-4" key={s.id}>
                <div className="card h-100">
                  <div className="card-body d-flex flex-column">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="fw-semibold">
                        <span className="text-muted">ID: </span>
                        <code>{s.id}</code>
                      </div>
                      <StatusBadge status={s.status} />
                    </div>

                    <div className="small text-muted mb-2">
                      {s.code ? (
                        <>
                          Code: <strong>{s.code}</strong>
                        </>
                      ) : (
                        <>Code: —</>
                      )}
                    </div>

                    <div className="mb-2">
                      <span className="fw-semibold">{s.origin || "—"}</span>
                      <span className="mx-1">→</span>
                      <span className="fw-semibold">{s.destination || "—"}</span>
                    </div>

                    <div className="small text-muted mb-3">
                      Créé: {fmtDate(s.createdAt)} <br />
                      Expédié: {fmtDate(s.shippedAt)}
                    </div>

                    <div className="mb-2">
                      <div className="d-flex justify-content-between">
                        <div className="small">
                          Articles: <strong>{lines.length}</strong>
                        </div>
                        <div className="small">
                          Reçu: <strong>{received}</strong> / {shipped}
                        </div>
                      </div>
                      <div
                        className="progress"
                        role="progressbar"
                        aria-valuenow={progress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                        style={{ height: 8 }}
                      >
                        <div
                          className="progress-bar"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>

                    <div className="mt-auto d-flex gap-2">
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => navigate(`/admin/shipments/${s.id}`)}
                      >
                        Voir les articles
                      </button>
                      <Link
                        className="btn btn-dark btn-sm"
                        to={`/admin/shipments/${s.id}/receive`}
                      >
                        Recevoir
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="d-flex justify-content-center my-3">
        <button
          className="btn btn-outline-secondary"
          disabled={loading || done}
          onClick={fetchMore}
        >
          {loading ? "Chargement…" : done ? "Plus de résultats" : "Charger plus"}
        </button>
      </div>
    </div>
  );
}